import _                                      from 'underscore';
import React, { useEffect, useRef, useState } from 'react';
import { Form, List, Segment }                from 'semantic-ui-react';
import { Formik, FormikProps }                from 'formik';
import * as Yup                               from 'yup';
import { useQuery }                           from '@apollo/react-hooks';

import { runIfFunction }                                                                                         from '@src/util';
import { Align, CorvalCategory, CorvalContextOwner, CorvalStaticProjectActivity, CorvalSubCategory, Size, Uuid } from '@src/types';
import { queriesHaveData, queriesLoading }                                                                       from '@src/store/_apolloUtil';
import { BasicDialogContainer }                                                                                  from '../../../components/modals/BasicDialogContainer';
import { CollapsibleContentContainer, StyledContainer }                                                          from '../../../containers';
import { LoadingIndicator }                                                                                      from '../../../components/util/loading/LoadingIndicator';
import { ENTITY_ICONS }                                                                                          from "@src/constants";
import { UseIf }                                                                                                 from "@src/views/components/display/EqualValues";
import { BigFriendlyMessage }                                                                                    from "@src/views/components/util/messaging/BigFriendlyMessage";
import { DecisionEngineQueries }                                                                                 from "@src/queries";
import { ActivityQueries }                                                                                       from "@src/queries/ActivityQueries";
import { BasicDialog, BasicDialogProps }                                                                         from "@src/views/components/modals/BasicDialog";

import { GenericEntitySelectionContainerLayout } from "@src/views/pages/projects/operations/entity-selection/GenericEntitySelectionContainerLayout";
import { GenericEntitySelectionLayout }          from "@src/views/pages/projects/operations/entity-selection/GenericEntitySelectionLayout";
import { HierarchyTaxonomyType }                 from "@src/views/pages/projects/mapping/gantt/tools/TaskFunctions";
import { FinePrintMessage }                      from "@src/views/components/util/messaging/FinePrintMessage";


export interface AddEmptyProjectTaxonomyDialogOnCompleteReturnData {
    taxonomy: HierarchyTaxonomyType[],
}

interface AddEmptyProjectTaxonomyDialogProps extends BasicDialogProps {
    projectId: Uuid,
    projectVersionId: string,
    categoryId?: Uuid,
    excludedTaxonomyIds: Uuid[],

    onComplete?( data: AddEmptyProjectTaxonomyDialogOnCompleteReturnData ): void,
}

interface FormValues {
    [ SUBCATEGORY_ID: string ]: boolean,
}

type EmptyStaticProjectSubCategoryDataItem = Pick<CorvalSubCategory, 'id' | 'name' | 'displayName' | 'description' | 'category' | 'ordinal'>
type EmptyStaticProjectSubCategoryStateData = Array<[ categoryId: string, subcategoryGroups: EmptyStaticProjectSubCategoryDataItem[] ]>

const AddEmptyProjectTaxonomyDialogDefaultProps = {
    projectId:           null,
    projectVersionId:    null,
    excludedTaxonomyIds: [],
    categoryId:          null,
} satisfies Partial<AddEmptyProjectTaxonomyDialogProps>

function AddEmptyProjectTaxonomyDialog( __props: AddEmptyProjectTaxonomyDialogProps ){

    const _props = { ...AddEmptyProjectTaxonomyDialogDefaultProps, ...__props };

    const { projectId, projectVersionId, excludedTaxonomyIds, categoryId: selectedCategoryId, ...props } = _props,
          [ emptyCategories, setEmptyCategories ]                                                        = useState<EmptyStaticProjectSubCategoryDataItem[]>( [] ),
          [ availableCategories, setAvailableCategories ]                                                = useState<EmptyStaticProjectSubCategoryDataItem[]>( [] ),
          [ availableSubCategories, setAvailableSubCategories ]                                          = useState<EmptyStaticProjectSubCategoryDataItem[]>( [] ),
          [ availableSubCategoriesByCategory, setAvailableSubCategoriesByCategory ]                      = useState<EmptyStaticProjectSubCategoryStateData>( [] ),
          formikRef                                                                                      = useRef<FormikProps<FormValues>>( null );

    // Get all DEM subcategories that are available
    const getAvailableCategoryReq = useQuery<{ categories: CorvalCategory[] }>( DecisionEngineQueries.queries.GET_CATEGORIES( `id name displayName ordinal description` ), {
        fetchPolicy: 'network-only',
        variables:   {
            where: {
                or: [
                    { contextOwner: { eq: CorvalContextOwner.Corval } },
                    { contextOwnerProjectId: { eq: projectId } }
                ]
            }
        }
    } );
    const getAvailableSubCategoryReq = useQuery<{ subCategories: CorvalSubCategory[] }>( DecisionEngineQueries.queries.GET_SUBCATEGORIES( `id name displayName ordinal description category{ id name displayName ordinal description }` ), {
        fetchPolicy: 'network-only',
        variables:   {
            where: {
                or: [
                    { contextOwner: { eq: CorvalContextOwner.Corval } },
                    { contextOwnerProjectId: { eq: projectId } }
                ]
            }
        }
    } );

    // Get all static project activities for the given project version
    const getStaticProjectActivitiesReq = useQuery<{ staticProjectActivites: CorvalStaticProjectActivity[] }>( ActivityQueries.queries.GET_PROJECT_ACTIVITIES( `id categoryId subCategoryId`, projectVersionId ), {
        variables:   { projectVersionId },
        skip:        ( !projectId || !projectVersionId ),
        fetchPolicy: 'network-only'
    } );

    const isLoading = queriesLoading( [ getAvailableCategoryReq, getAvailableSubCategoryReq, getStaticProjectActivitiesReq ] );

    useEffect( () => {

        if( !queriesLoading( [ getAvailableCategoryReq, getAvailableSubCategoryReq, getStaticProjectActivitiesReq ] ) &&
            queriesHaveData( [ getAvailableCategoryReq, getAvailableSubCategoryReq, getStaticProjectActivitiesReq ] ) ){

            // pluck unique subcategory records
            const activitySubCategoryIds = _.chain( getStaticProjectActivitiesReq.data.staticProjectActivites )
                                            .pluck( 'subCategoryId' )
                                            .uniq()
                                            .value();

            // Get subcategories from the DEM that aren't in the project, format records
            const subCategoryItems = ( getAvailableSubCategoryReq.data.subCategories ).filter( subcategory => (
                !activitySubCategoryIds.includes( subcategory.id ) &&
                !excludedTaxonomyIds.includes( subcategory.id )
            ) ) as EmptyStaticProjectSubCategoryDataItem[];

            const categoryItems = _.chain( subCategoryItems )
                                   .pluck( 'category' )
                                   .uniq( cat => cat.id )
                                   .filter( cat => !excludedTaxonomyIds.includes( cat.id ) )
                                   .value();

            // Format the subcategories for taxonomy layout
            let categoricalItems = _.chain( subCategoryItems )
                                    .filter( ( s: EmptyStaticProjectSubCategoryDataItem ) => ( !selectedCategoryId || s.category.id === selectedCategoryId ) )
                                    .groupBy( ( s: EmptyStaticProjectSubCategoryDataItem ) => s.category.id )
                                    .pairs()
                                    .value();

            // Find all categories that do not contain sub items
            let emptyCategories = _.filter( getAvailableCategoryReq.data.categories, category => !_.map( categoricalItems, ( [ associatedCatId ] ) => associatedCatId ).includes( category.id ) );

            // if we are trying to get a specific category, and nothing showed up,
            // hide any empty categories, since they are found by differential
            if( !_.isEmpty( categoricalItems ) || ( _.isEmpty( categoricalItems ) && !_.isEmpty( selectedCategoryId ) ) ){
                emptyCategories = [];
            }

            // Create empty items in category set
            emptyCategories.forEach( ( category ) => {
                categoricalItems.push( [ category.id, [] ] );
            } )

            // Collect the name of each category
            categoricalItems = _.sortBy( categoricalItems, ( [ categoryId, subcategoryGroup ] ) => {
                return !_.isEmpty( subcategoryGroup )
                       ? _.first( subcategoryGroup ).category.name
                       : _.findWhere( emptyCategories, { id: categoryId } )?.name ?? 'Unknown Category Name'
            } );

            setEmptyCategories( emptyCategories )
            setAvailableCategories( categoryItems )
            setAvailableSubCategories( subCategoryItems )
            setAvailableSubCategoriesByCategory( categoricalItems )
        }

    }, [ getAvailableSubCategoryReq, getStaticProjectActivitiesReq ] );

    const handleOnSubmit = async () => {

        return Promise.resolve( formikRef.current.values );
    }

    const handleOnComplete = ( values: Record<any, boolean> ) => {

        const selectedItems = [];

        _.mapObject( values, ( isSelected, selectedId ) => {

            if( isSelected ){
                const selectedSubCategory   = _.findWhere( availableSubCategories, { id: selectedId } ),
                      selectedCategory      = _.findWhere( availableCategories, { id: selectedId } ),
                      selectedEmptyCategory = _.findWhere( emptyCategories, { id: selectedId } );

                if( selectedEmptyCategory ){

                    selectedItems.push( {
                        category:    selectedEmptyCategory,
                        subCategory: null,
                    } as HierarchyTaxonomyType );

                } else if( selectedCategory ){

                    // check to make sure categories aren't being added twice
                    const selectedSubCategoriesInSameCategory = _.filter( availableSubCategories, subCat => ( ( values[ subCat.id ] === true ) && subCat.category.id === selectedId ) );

                    if( _.isEmpty( selectedSubCategoriesInSameCategory ) ){

                        selectedItems.push( {
                            category:    selectedCategory,
                            subCategory: null,
                        } as HierarchyTaxonomyType );
                    }

                } else if( selectedSubCategory ){

                    selectedItems.push( {
                        category:    selectedSubCategory.category,
                        subCategory: _.omit( selectedSubCategory, 'category' ),
                    } as HierarchyTaxonomyType );
                }
            }
        } )

        runIfFunction( props.onComplete, { taxonomy: selectedItems } );
    }

    // Set Formik initial values
    let initialValues: FormValues = {};

    // If ready, set initial values to all ids of cats / subcats that can exist on form
    if( !isLoading && !_.isEmpty( availableCategories ) && !_.isEmpty( availableSubCategories ) ){

        _.pluck( [ ...availableCategories, ...availableSubCategories ], 'id' ).map( id => {
            initialValues[ id ] = false;
        } )
    }

    return (

        <Formik<FormValues>
            innerRef={ formikRef }
            initialValues={ initialValues }
            isInitialValid={ false }
            enableReinitialize={ true }
            validateOnChange={ true }
            validationSchema={ Yup.object( _.mapObject( initialValues, () => Yup.boolean() ) ) }
            onSubmit={ null }>

            { formik => (

                isLoading
                ? (
                    <StyledContainer padding={ Size.LG }>
                        <LoadingIndicator dimmer={ false } isActive={ true } label={ 'Loading Available Content...' }/>
                    </StyledContainer>
                ) : (
                    <BasicDialog
                        { ...props }
                        operationDisabled={ !projectId || !projectVersionId || isLoading || !formik.isValid }
                        operationIsHidden={ !projectId || !projectVersionId || ( availableSubCategories.length === 0 ) }
                        onSubmit={ handleOnSubmit }
                        onComplete={ handleOnComplete }
                        submitLabel={ 'Add Content' }
                        className={ 'no-padding' }
                        alignControls={ Align.CENTER }>

                        <Form>

                            <BasicDialogContainer
                                title={ 'Import Project Organization and Content' }
                                contentMaxHeight={ '60dvh' }
                                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                                overflow={ true }
                                scrolling={ true }
                                roundedTop>

                                <UseIf x={ availableSubCategoriesByCategory.length } eq={ 0 }>
                                    <BigFriendlyMessage
                                        title={ 'Selected Project Organization and Content Added!' }
                                        content={ (
                                            <>
                                                <p>There are no additional categories available to add to this project.</p>
                                                <p>Check back later for more categories your team might have created, or create new ones with the help of an admin.</p>
                                            </>
                                        ) }
                                    />
                                </UseIf>

                                {/* List of categories to select */ }
                                { availableSubCategoriesByCategory.map( ( [ categoryId, subcategoryGroup ] ) => {

                                    const isEmptyCategory         = _.isEmpty( subcategoryGroup ),
                                          categoryIsAvailable     = _.pluck( availableCategories, 'id' ).includes( categoryId ),
                                          isSelectingFromCategory = !_.isEmpty( selectedCategoryId ),
                                          categoryName            = _.isEmpty( subcategoryGroup )
                                                                    ? emptyCategories.find( o => o.id === categoryId )?.name
                                                                    : availableSubCategories.find( o => o.category.id === categoryId )?.category.name,
                                          categoryDescription     = _.isEmpty( subcategoryGroup )
                                                                    ? emptyCategories.find( o => o.id === categoryId )?.description
                                                                    : availableSubCategories.find( o => o.category.id === categoryId )?.category.description;

                                    return (
                                        <Segment key={ categoryId } raised className={ 'no-padding' }>

                                            <CategoryContainer
                                                isActive={ _.isString( selectedCategoryId ) }
                                                categoryId={ categoryId }
                                                categoryName={ categoryName }>

                                                <Segment secondary>

                                                    <List>

                                                        {/* List category entry */ }
                                                        <UseIf x={ !isSelectingFromCategory && categoryIsAvailable } eq={ true }>

                                                            <List.Item key={ categoryId }>

                                                                <GenericEntitySelectionContainerLayout
                                                                    id={ categoryId }
                                                                    isSelected={ !!( formik.values[ categoryId ] ) }
                                                                    iconName={ ENTITY_ICONS.CATEGORIES.GENERIC }
                                                                    onClick={ () => {
                                                                        formik.setFieldValue( categoryId, !formik.values[ categoryId ] )
                                                                        subcategoryGroup.map( availableSubCategoriesInCategory => {
                                                                            formik.setFieldValue( availableSubCategoriesInCategory.id, false );
                                                                        } )
                                                                    } }>

                                                                    <GenericEntitySelectionLayout
                                                                        id={ categoryId }
                                                                        name={ 'Category Only' }
                                                                        description={ categoryDescription }
                                                                        compact={ true }
                                                                        fieldValueName={ categoryId }
                                                                        onChange={ ( e, data ) => {
                                                                            formik.setFieldValue( categoryId, data.checked )
                                                                            subcategoryGroup.map( availableSubCategoriesInCategory => {
                                                                                formik.setFieldValue( availableSubCategoriesInCategory.id, false );
                                                                            } )
                                                                        } }
                                                                    />

                                                                </GenericEntitySelectionContainerLayout>

                                                            </List.Item>

                                                        </UseIf>

                                                        <UseIf x={ isEmptyCategory } eq={ true }>

                                                            <List.Item key={ 'no-subitems' }>

                                                                <FinePrintMessage className={ 'text-center padding-md' }>This category does not have any subcategories yet.</FinePrintMessage>

                                                            </List.Item>

                                                        </UseIf>

                                                        {/* List all subcategories */ }
                                                        { subcategoryGroup.map( availableSubCategoriesInCategory => {

                                                            const { id, name, description } = availableSubCategoriesInCategory;

                                                            return (
                                                                <List.Item key={ id }>

                                                                    <GenericEntitySelectionContainerLayout
                                                                        id={ id }
                                                                        isSelected={ !!( formik.values[ id ] ) }
                                                                        iconName={ ENTITY_ICONS.CATEGORIES.ENTITY }
                                                                        onClick={ () => {
                                                                            formik.setFieldValue( id, !formik.values[ id ] )
                                                                            if( formik.values[ categoryId ] === true ){
                                                                                formik.setFieldValue( categoryId, false )
                                                                            }
                                                                        } }>

                                                                        <GenericEntitySelectionLayout
                                                                            id={ id }
                                                                            name={ name }
                                                                            fieldValueName={ id }
                                                                            description={ description }
                                                                            onChange={ ( e, data ) => {
                                                                                formik.setFieldValue( id, data.checked )
                                                                                if( formik.values[ categoryId ] === true ){
                                                                                    formik.setFieldValue( categoryId, false )
                                                                                }
                                                                            } }
                                                                        />

                                                                    </GenericEntitySelectionContainerLayout>

                                                                </List.Item>
                                                            )
                                                        } ) }


                                                    </List>

                                                </Segment>

                                            </CategoryContainer>

                                        </Segment>
                                    )
                                } ) }

                            </BasicDialogContainer>
                        </Form>

                    </BasicDialog>
                )
            ) }

        </Formik>
    )
}

const CategoryContainer = React.memo( ( props: {
    categoryId?: Uuid,
    categoryName: string,
    isActive?: boolean,
    children: any
} ) => (

    <CollapsibleContentContainer
        isActive={ props.isActive }
        borderless={ true }
        contentClasses={ 'padding-md padding-lg-left' }
        title={ props.categoryName }>

        { props.children }

    </CollapsibleContentContainer>
) );

export {
    AddEmptyProjectTaxonomyDialog,
    AddEmptyProjectTaxonomyDialogDefaultProps,
    type AddEmptyProjectTaxonomyDialogProps,
};
